import type { UseSpreadsheetNoteTabs } from 'components/SpreadsheetNotes/SpreadsheetNoteConfig/UseSpreadsheetNoteTabs/useSpreadsheetNoteTabs.props';
import type {
  Ordinate,
  SheetAttributeReferenceData,
} from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';

const getIndicatorReferenceCount = (indicatorGroups: Ordinate[][]) => (
  indicatorGroups.reduce((count, ordinates) => count + ordinates.length, 0)
);

const getMappedCellsReferenceCount = (
  referenceData: SheetAttributeReferenceData[],
  sheetName: string,
  documentId: number
) => (
  referenceData.reduce((count, reference) => {
    if (reference.documentReferenceId !== documentId) {
      return count;
    }

    return count + (reference.sheetMappingConfig?.[sheetName]?.mappedCells?.length ?? 0);
  }, 0)
);

const getFanOutSheetReferenceCount = (
  attributeMapper: UseSpreadsheetNoteTabs['attributeMapper'],
  sheetName: string,
  documentId: number
) => (
  Object.values(attributeMapper?.sheetAttributeMapping.data ?? {}).reduce(
    (columnCount, rowMetrics) =>
      columnCount +
      Object.values(rowMetrics).reduce(
        (rowCount, { referenceData }) =>
          rowCount + getMappedCellsReferenceCount(referenceData ?? [], sheetName, documentId),
        0
      ),
    0
  )
);

export const getSheetReferenceCount = (
  attributeMapper: UseSpreadsheetNoteTabs['attributeMapper'],
  sheetName: string,
  documentId: number
) => {
  const sheetMapping = attributeMapper?.sheetAttributeMapping.sheetIndicatorMapping.find(
    mapping => mapping.documentReferenceId === documentId && mapping.sheet === sheetName
  );
  if (!sheetMapping) {
    return undefined;
  }

  const fanOutReferenceCount = getFanOutSheetReferenceCount(attributeMapper, sheetName, documentId);
  if (fanOutReferenceCount > 0) {
    return fanOutReferenceCount;
  }

  const columnReferenceCount = getIndicatorReferenceCount(Object.values(sheetMapping.gridIndicators.columns));
  const rowReferenceCount = getIndicatorReferenceCount(Object.values(sheetMapping.gridIndicators.rows));
  const gridReferenceCount = columnReferenceCount > 0 && rowReferenceCount > 0
    ? columnReferenceCount * rowReferenceCount
    : 0;
  const referenceCount = sheetMapping.singleCellIndicators.length + gridReferenceCount;

  return referenceCount > 0 ? referenceCount : undefined;
};
